import React, { useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import AnalyticsService from '../../services/AnalyticsService';

interface ChartData {
  date: string;
  activeUsers: number;
  newUsers: number;
  sessions: number;
}

const UserEngagementChart: React.FC = () => {
  const [data, setData] = useState<ChartData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chartType, setChartType] = useState<'line' | 'area'>('area');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await AnalyticsService.fetchUserTrends();

      // Transform the data for the chart
      const chartData: ChartData[] = response.data.rows
        .map(row => {
          // Format date from YYYYMMDD to MM/DD
          const dateStr = row.date;
          const year = dateStr.substring(0, 4);
          const month = dateStr.substring(4, 6);
          const day = dateStr.substring(6, 8);
          const formattedDate = `${month}/${day}`;

          return {
            date: formattedDate,
            dateValue: dateStr, // Keep original for sorting
            activeUsers: parseInt(row.activeUsers || 0),
            newUsers: parseInt(row.newUsers || 0),
            sessions: parseInt(row.sessions || 0)
          };
        })
        .sort((a, b) => a.dateValue.localeCompare(b.dateValue)) // Sort by date
        .map(({ dateValue, ...rest }) => rest); // Remove dateValue after sorting

      setData(chartData);
    } catch (err) {
      console.error('Error fetching user trends:', err);
      setError('Failed to load user engagement data');
    } finally {
      setLoading(false);
    }
  };

  const handleChartTypeChange = (event: SelectChangeEvent) => {
    setChartType(event.target.value as 'line' | 'area');
  };

  const renderChart = () => {
    if (chartType === 'line') {
      return (
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Line
            type="monotone"
            dataKey="activeUsers"
            stroke="#1976d2"
            name="Active Users"
            strokeWidth={2}
          />
          <Line
            type="monotone"
            dataKey="newUsers"
            stroke="#2e7d32"
            name="New Users"
            strokeWidth={2}
          />
          <Line
            type="monotone"
            dataKey="sessions"
            stroke="#ed6c02"
            name="Sessions"
            strokeWidth={2}
          />
        </LineChart>
      );
    }

    return (
      <AreaChart data={data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="date" />
        <YAxis />
        <Tooltip />
        <Legend />
        <Area
          type="monotone"
          dataKey="sessions"
          stackId="1"
          stroke="#ed6c02"
          fill="#ed6c02"
          fillOpacity={0.6}
          name="Sessions"
        />
        <Area
          type="monotone"
          dataKey="activeUsers"
          stackId="2"
          stroke="#1976d2"
          fill="#1976d2"
          fillOpacity={0.6}
          name="Active Users"
        />
        <Area
          type="monotone"
          dataKey="newUsers"
          stackId="3"
          stroke="#2e7d32"
          fill="#2e7d32"
          fillOpacity={0.6}
          name="New Users"
        />
      </AreaChart>
    );
  };

  return (
    <Paper sx={{ p: 3, height: '400px', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" component="h2">
          User Engagement Trends (30 Days)
        </Typography>
        {!loading && !error && data.length > 0 && (
          <FormControl size="small">
            <Select value={chartType} onChange={handleChartTypeChange}>
              <MenuItem value="area">Area Chart</MenuItem>
              <MenuItem value="line">Line Chart</MenuItem>
            </Select>
          </FormControl>
        )}
      </Box>

      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}

        {!loading && !error && data.length > 0 && (
          <ResponsiveContainer width="100%" height="100%">
            {renderChart()}
          </ResponsiveContainer>
        )}

        {!loading && !error && data.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography color="text.secondary">No data available</Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default UserEngagementChart;